/**
 * @voltxampere/spice — JFET codec (J)
 *
 * Berkeley SPICE3 syntax: `J{name} ND NG NS MODEL_NAME [area]`
 *
 * Examples:
 *   J1 d g s J2N3819
 *   J2 drain gate src NJF
 *
 * Schema mapping: `type: "jfet"`, params: `{ model, channel }`.
 * Channel polarity (`n` or `p`) inferred from model name; defaults
 * to `n`.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ok, err, } from "./_base.js";
import { stringParam } from "./_param-helpers.js";
const PJF_MODELS = new Set(["PJF", "PJF_DEFAULT", "J2N5460"]);
const NJF_MODELS = new Set(["NJF", "NJF_DEFAULT", "J2N3819", "J2N5457", "BF245"]);
function classifyJfetModel(model) {
    const upper = model.toUpperCase();
    if (PJF_MODELS.has(upper) || upper.startsWith("PJF"))
        return "p";
    if (NJF_MODELS.has(upper) || upper.startsWith("NJF"))
        return "n";
    return "n";
}
export const jfetCodec = {
    spiceLetter: "J",
    schemaTypes: ["jfet"],
    examples: ["J1 d g s J2N3819", "J2 drain gate src NJF"],
    parse(card, _ctx) {
        if (card.tokens.length < 4) {
            return err("J needs 3 nodes and a model name");
        }
        const [nd, ng, ns, modelRaw] = card.tokens;
        if (nd === undefined || ng === undefined || ns === undefined || modelRaw === undefined) {
            return err("J has incomplete tokens");
        }
        const channel = classifyJfetModel(modelRaw);
        const params = {
            model: modelRaw,
            channel,
        };
        const component = {
            id: card.identifier,
            type: "jfet",
            position: { x: 0, y: 0 },
            rotation: 0,
            flipped: false,
            label: card.identifier,
            params,
            terminals: [],
        };
        return ok(component, [nd, ng, ns]);
    },
    emit(component, terminals) {
        const channel = stringParam(component.params.channel, "n");
        const fallback = channel === "p" ? "PJF" : "NJF";
        const model = stringParam(component.params.model, fallback);
        const [d, g, s] = terminals;
        return `${component.id} ${d ?? "0"} ${g ?? "0"} ${s ?? "0"} ${model}`;
    },
    owns(schemaType) {
        return schemaType === "jfet";
    },
};
//# sourceMappingURL=jfet.js.map